import { useEffect, useMemo, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useUserStore } from '@/stores/userStore'
import { useWorkoutStore, type PRRecord, type ExerciseHistoryEntry } from '@/stores/workoutStore'
import { getWorkoutDisplayName } from '@/lib/utils'
import { getWeekProgression, isDeloadWeek, type Phase, type Week } from '@/data/xframe-templates'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts'
import { format, parseISO } from 'date-fns'
import type { WorkoutType } from '@/types/common'

const WORKOUT_TYPES: { type: WorkoutType; muscles: string }[] = [
  { type: 'chest_push', muscles: 'Chest, Delts, Triceps' },
  { type: 'pull_a', muscles: 'Back, Biceps' },
  { type: 'legs', muscles: 'Quads, Hams, Calves' },
  { type: 'shoulder_push', muscles: 'Delts, Chest, Triceps' },
  { type: 'pull_b', muscles: 'Back, Biceps' },
  { type: 'ham_legs', muscles: 'Hams, Quads, Calves' },
]

function PhaseSelector() {
  const { profile, setPhase, setWeek } = useUserStore()

  if (!profile) return null

  return (
    <div className="card">
      <h2 className="text-sm font-medium text-text-secondary mb-3">Program Position</h2>

      {/* Phase selector */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        {[1, 2].map((phase) => (
          <button
            key={phase}
            onClick={() => setPhase(phase as 1 | 2)}
            className={`py-2 px-4 rounded-lg font-medium transition-colors ${
              profile.current_phase === phase
                ? 'bg-primary text-white'
                : 'bg-elevated text-text-secondary hover:text-text-primary'
            }`}
          >
            Phase {phase}
          </button>
        ))}
      </div>

      {/* Week selector */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-text-muted">Week:</span>
        <div className="flex gap-1 flex-wrap">
          {([1, 2, 3, 4, 5, 6, 7, 8] as const).map((week) => (
            <button
              key={week}
              onClick={() => setWeek(week)}
              className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                profile.current_week === week
                  ? isDeloadWeek(week)
                    ? 'bg-success text-white'
                    : 'bg-primary text-white'
                  : isDeloadWeek(week)
                  ? 'bg-success/20 text-success hover:bg-success/30'
                  : 'bg-elevated text-text-secondary hover:text-text-primary'
              }`}
            >
              {week}
            </button>
          ))}
        </div>
      </div>

      <p className="text-xs text-text-muted mt-2">
        Volume Tier {profile.volume_tier}
        {isDeloadWeek(profile.current_week) && (
          <span className="text-success ml-2">• Deload Week</span>
        )}
      </p>
    </div>
  )
}

function RIRGuide({ phase, week }: { phase: Phase; week: Week }) {
  const progression = getWeekProgression(phase, week)
  const isDeload = isDeloadWeek(week)

  return (
    <div className="card bg-surface">
      <h3 className="font-medium text-text-primary mb-3">
        Week {week} Training Targets
      </h3>

      {isDeload ? (
        <div className="text-center py-2">
          <p className="text-success font-semibold text-lg">Deload Week</p>
          <p className="text-sm text-text-muted mt-1">
            50% volume, RIR 3 on all exercises
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-elevated rounded-lg">
              <p className="text-xs text-text-muted uppercase tracking-wide">Compounds</p>
              <p className="text-2xl font-bold text-primary mt-1">
                RIR {progression.rir.compound}
              </p>
            </div>
            <div className="text-center p-3 bg-elevated rounded-lg">
              <p className="text-xs text-text-muted uppercase tracking-wide">Isolation</p>
              <p className="text-2xl font-bold text-accent mt-1">
                RIR {progression.rir.isolation}
              </p>
            </div>
          </div>

          {/* Intensifier */}
          {progression.intensifier && (
            <div className="mt-3 pt-3 border-t border-elevated">
              <p className="text-xs text-text-muted mb-2">Intensifier Available:</p>
              <div className="flex flex-wrap gap-2">
                {progression.intensifier === 'rest_pause' && (
                  <span className="px-2 py-1 bg-warning/20 text-warning text-xs rounded-full">
                    Rest-Pause on final sets
                  </span>
                )}
                {progression.intensifier === 'drop_set' && (
                  <span className="px-2 py-1 bg-error/20 text-error text-xs rounded-full">
                    Drop Sets on final sets
                  </span>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function WorkoutCard({
  type,
  muscles,
  completedThisWeek,
  lastCompleted,
}: {
  type: WorkoutType
  muscles: string
  completedThisWeek: boolean
  lastCompleted?: string
}) {
  return (
    <Link
      to={`/workout?type=${type}`}
      className="card hover:bg-elevated transition-colors"
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-text-primary">
              {getWorkoutDisplayName(type)}
            </h3>
            {completedThisWeek && (
              <span className="px-2 py-0.5 bg-success/20 text-success text-xs rounded-full">
                Done
              </span>
            )}
          </div>
          <p className="text-sm text-text-muted">{muscles}</p>
          {lastCompleted && (
            <p className="text-xs text-text-muted mt-1">
              Last: {new Date(lastCompleted).toLocaleDateString()}
            </p>
          )}
        </div>

        {completedThisWeek ? (
          <div className="w-10 h-10 bg-success/20 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        ) : (
          <svg className="w-6 h-6 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        )}
      </div>
    </Link>
  )
}

function WorkoutHistory() {
  const { sessions, loading } = useWorkoutStore()
  const [showAll, setShowAll] = useState(false)

  const recentSessions = useMemo(() => {
    const completed = sessions.filter((s) => s.completed)
    return showAll ? completed : completed.slice(0, 5)
  }, [sessions, showAll])

  if (loading) {
    return (
      <div className="card">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-elevated rounded w-1/3"></div>
          <div className="h-12 bg-elevated rounded"></div>
          <div className="h-12 bg-elevated rounded"></div>
        </div>
      </div>
    )
  }

  if (recentSessions.length === 0) {
    return (
      <div className="card text-center py-6">
        <p className="text-text-muted">No workout history yet</p>
        <p className="text-sm text-text-muted mt-1">
          Complete your first workout to see it here
        </p>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-text-primary">Workout History</h3>
        {sessions.filter((s) => s.completed).length > 5 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-sm text-primary"
          >
            {showAll ? 'Show Less' : 'Show All'}
          </button>
        )}
      </div>

      <div className="space-y-2">
        {recentSessions.map((session) => (
          <div
            key={session.id}
            className="flex items-center justify-between p-3 bg-elevated rounded-lg"
          >
            <div>
              <p className="font-medium text-text-primary">
                {getWorkoutDisplayName(session.workout_type as WorkoutType)}
              </p>
              <p className="text-xs text-text-muted">
                {new Date(session.date).toLocaleDateString()} • P{session.phase} W{session.week}
              </p>
            </div>
            <div className="text-right">
              {session.total_volume_kg && (
                <p className="text-sm font-medium text-primary">
                  {session.total_volume_kg.toLocaleString()} kg
                </p>
              )}
              {session.duration_minutes && (
                <p className="text-xs text-text-muted">
                  {session.duration_minutes} min
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================
// PR BOARD & EXERCISE HISTORY
// ============================================

function PRBoard() {
  const { profile } = useUserStore()
  const { getAllPRs, getExerciseHistory } = useWorkoutStore()
  const [prs, setPRs] = useState<PRRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedExercise, setSelectedExercise] = useState<string | null>(null)
  const [exerciseHistory, setExerciseHistory] = useState<ExerciseHistoryEntry[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)

  const isMetric = profile?.unit_system === 'metric'
  const unitLabel = isMetric ? 'kg' : 'lbs'

  // Load all PRs on mount
  useEffect(() => {
    const loadPRs = async () => {
      setLoading(true)
      const allPRs = await getAllPRs()
      setPRs(allPRs)
      setLoading(false)
    }
    loadPRs()
  }, [getAllPRs])

  // Load exercise history when an exercise is selected
  const loadHistory = useCallback(async (exerciseName: string) => {
    setHistoryLoading(true)
    setSelectedExercise(exerciseName)
    const history = await getExerciseHistory(exerciseName, 15)
    setExerciseHistory(history)
    setHistoryLoading(false)
  }, [getExerciseHistory])

  // Convert weight for display
  const displayWeight = (kg: number) => {
    if (isMetric) return Math.round(kg)
    return Math.round(kg * 2.20462)
  }

  // Prepare chart data
  const chartData = useMemo(() => {
    return exerciseHistory
      .slice()
      .reverse()
      .map(entry => ({
        date: format(parseISO(entry.date), 'MMM d'),
        weight: displayWeight(entry.bestSet.weightKg),
        reps: entry.bestSet.reps,
        estimated1RM: displayWeight(entry.bestSet.estimatedOneRM),
      }))
  }, [exerciseHistory, isMetric])

  if (loading) {
    return (
      <div className="card">
        <h2 className="text-lg font-semibold text-text-primary mb-3">Personal Records</h2>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
        </div>
      </div>
    )
  }

  if (prs.length === 0) {
    return (
      <div className="card">
        <h2 className="text-lg font-semibold text-text-primary mb-3">Personal Records</h2>
        <div className="text-center py-8 text-text-muted">
          <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p>No PRs yet</p>
          <p className="text-sm">Complete workouts to track your records!</p>
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <h2 className="text-lg font-semibold text-text-primary mb-3">Personal Records</h2>

      {/* Exercise History Modal/Detail */}
      {selectedExercise && (
        <div className="mb-4 p-3 bg-elevated rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-text-primary">{selectedExercise}</h3>
            <button
              onClick={() => setSelectedExercise(null)}
              className="text-text-muted hover:text-text-primary"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {historyLoading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary" />
            </div>
          ) : chartData.length > 1 ? (
            <>
              {/* Strength progression chart */}
              <p className="text-xs text-text-muted mb-2">Estimated 1RM Progression</p>
              <div className="h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <XAxis
                      dataKey="date"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7280', fontSize: 10 }}
                    />
                    <YAxis
                      hide
                      domain={['dataMin * 0.95', 'dataMax * 1.05']}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1f2937',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                      labelStyle={{ color: '#9ca3af' }}
                      formatter={(value: number, name: string) => {
                        if (name === 'estimated1RM') return [`${value} ${unitLabel}`, 'Est. 1RM']
                        if (name === 'weight') return [`${value} ${unitLabel}`, 'Weight']
                        return [value, name]
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="estimated1RM"
                      stroke="#0ea5e9"
                      strokeWidth={2}
                      dot={{ fill: '#0ea5e9', strokeWidth: 0, r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Recent sets */}
              <div className="mt-3">
                <p className="text-xs text-text-muted mb-2">Recent Sessions</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {exerciseHistory.slice(0, 5).map((entry) => (
                    <div key={entry.sessionId} className="flex items-center justify-between text-sm py-1 border-b border-border/50 last:border-0">
                      <span className="text-text-muted">{format(parseISO(entry.date), 'MMM d')}</span>
                      <span className="text-text-primary">
                        {displayWeight(entry.bestSet.weightKg)} {unitLabel} × {entry.bestSet.reps}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <p className="text-center text-text-muted py-4 text-sm">
              Need more sessions to show progression
            </p>
          )}
        </div>
      )}

      {/* PR List */}
      <div className="space-y-2">
        {prs.slice(0, 8).map((pr) => (
          <button
            key={pr.exerciseName}
            onClick={() => loadHistory(pr.exerciseName)}
            className={`w-full flex items-center justify-between p-2 rounded-lg transition-colors ${
              selectedExercise === pr.exerciseName
                ? 'bg-primary/10 border border-primary/30'
                : 'bg-elevated hover:bg-surface'
            }`}
          >
            <div className="text-left">
              <p className="font-medium text-text-primary text-sm">{pr.exerciseName}</p>
              <p className="text-xs text-text-muted">
                {format(parseISO(pr.date), 'MMM d, yyyy')}
              </p>
            </div>
            <div className="text-right">
              <p className="font-bold text-primary">
                {displayWeight(pr.weightKg)} {unitLabel} × {pr.reps}
              </p>
              <p className="text-xs text-text-muted">
                Est 1RM: {displayWeight(pr.estimatedOneRM)} {unitLabel}
              </p>
            </div>
          </button>
        ))}
      </div>

      {prs.length > 8 && (
        <p className="text-xs text-text-muted text-center mt-3">
          +{prs.length - 8} more exercises
        </p>
      )}
    </div>
  )
}

export default function Logbook() {
  const { profile } = useUserStore()
  const { sessions, loadSessions, getCompletedDatesThisWeek } = useWorkoutStore()

  // Load sessions on mount
  useEffect(() => {
    if (profile?.id) {
      loadSessions(profile.id)
    }
  }, [profile?.id, loadSessions])

  // Calculate completion status for each workout type this week
  const completedThisWeek = useMemo(() => {
    const dates = getCompletedDatesThisWeek()
    const workoutTypes = new Set<string>()

    sessions
      .filter((s) => s.completed && dates.includes(s.date.split('T')[0]))
      .forEach((s) => workoutTypes.add(s.workout_type))

    return workoutTypes
  }, [sessions, getCompletedDatesThisWeek])

  // Get last completed date for each workout type
  const lastCompletedDates = useMemo(() => {
    const dates: Record<string, string> = {}

    sessions
      .filter((s) => s.completed)
      .forEach((s) => {
        if (!dates[s.workout_type] || s.date > dates[s.workout_type]) {
          dates[s.workout_type] = s.date
        }
      })

    return dates
  }, [sessions])

  return (
    <div className="p-4 space-y-4 pb-24">
      <PhaseSelector />

      {profile && (
        <RIRGuide phase={profile.current_phase} week={profile.current_week} />
      )}

      <div>
        <h2 className="text-lg font-semibold text-text-primary mb-3">
          Choose Workout
        </h2>
        <div className="grid gap-3">
          {WORKOUT_TYPES.map((workout) => (
            <WorkoutCard
              key={workout.type}
              type={workout.type}
              muscles={workout.muscles}
              completedThisWeek={completedThisWeek.has(workout.type)}
              lastCompleted={lastCompletedDates[workout.type]}
            />
          ))}
        </div>
      </div>

      <PRBoard />

      <WorkoutHistory />
    </div>
  )
}
